// @/Hooks/ClassroomInfoApi/useClassroomJoin.ts
import {useRef, useState} from 'react';
import {ScreenName} from '@/navigators/StackConstants';
import useGetActiveMeetingsApi from './useGetActiveMeetingsApi';
import useClassroomEndMeetingApi from './useClassroomEndMeetingApi';
import {openPopup} from '@/Utils/CommonUtils';
import useRestrictClassModalStore from '@/Store/RestrictClassModalStore';

interface JoinData {
  webinar_id: string;
  registrant_token: string;
  meeting_password: string;
  occurence_id: string;
  meeting_role: string;
  is_zoom_meeting: number | boolean;
  batch_type: string;
  webinar_topic: string;
  is_test_class?: boolean;
}

interface UseClassroomJoinReturn {
  showActiveMeetingsModal: boolean;
  activeMeetingsCount: number;
  activeLoading: boolean;
  endLoading: boolean;
  handleJoinClick: (joinData: JoinData) => boolean;
  handleModalClose: () => void;
  handleModalJoin: () => void;
  handleModalEndAndJoin: () => void;
}
// Add Safari detection helper
const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;

  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
  const desktopSafari = /^((?!chrome|android).)*safari/i.test(ua);

  return iOSSafari || desktopSafari;
};

export default function useClassroomJoin(): UseClassroomJoinReturn {
  const hasCheckedPopupRef = useRef(false);
  const popupRef = useRef<Window | null>(null); // For Safari pre-opened popup
  const [showActiveMeetingsModal, setShowActiveMeetingsModal] = useState(false);
  const [activeMeetingsData, setActiveMeetingsData] = useState<any>(null);
  const [pendingJoinData, setPendingJoinData] = useState<JoinData | null>(null);
  const {
    showRestrictClassModal,
    setShowRestrictClassModal,
    setShowJoinStatusMessage,
  } = useRestrictClassModalStore();
  // Modified joinClassroom
  const joinClassroom = (joinData: JoinData, existingPopup?: Window | null) => {
    localStorage.setItem(
      'ZoomUserData',
      JSON.stringify({
        token: joinData.registrant_token,
        meetingId: joinData.webinar_id,
        password: joinData.meeting_password,
        occurenceId: joinData.occurence_id,
        meetingRole: joinData.meeting_role,
        isZoomMeeting: joinData.is_zoom_meeting,
        batchType: joinData.batch_type,
        topic: joinData.webinar_topic,
        isTestClass: joinData.is_test_class ?? false,
      }),
    );
    //  const url = `${window.location.origin}/${ScreenName.Classroom}`
    const url = 'https://learn.circlechess.com/Classroom';
    let isPopupOpened = false;
    // Safari: Use pre-opened popup
    if (isSafari() && existingPopup && !existingPopup.closed) {
      existingPopup.location.href = url;
      existingPopup.focus();
      isPopupOpened = true;
    }
    // Other browsers: Use existing openPopup utility
    else {
      isPopupOpened = openPopup(url);
    }
    if (!hasCheckedPopupRef.current) {
      hasCheckedPopupRef.current = true;
    }
    if (!isPopupOpened) {
      return false;
    }

    setPendingJoinData(null);
    popupRef.current = null; // Clear Safari ref
    return true;
  };

  const handleOnSuccessGetActiveMeetings = (data: any) => {
    console.log('Active meetings response:', data);

    if (data?.success && data?.join_status_message) {
      // Close Safari pre-opened popup if exists
      if (isSafari() && popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
      }
      setShowRestrictClassModal(true);
      setShowJoinStatusMessage(data?.join_status_message);
      return;
    } else if (data?.success && data?.active_meetings_count > 0) {
      // Close Safari pre-opened popup if exists
      if (isSafari() && popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
      }
      setActiveMeetingsData(data);
      setShowActiveMeetingsModal(true);
    } else {
      if (pendingJoinData) {
        // Pass pre-opened popup only for Safari
        const opened = joinClassroom(
          pendingJoinData,
          isSafari() ? popupRef.current : null,
        );
        if (!opened) {
          return;
        }
      }
    }
  };

  const handleOnErrorGetActiveMeetings = (error: any) => {
    console.error('Error fetching active meetings:', error);
    if (pendingJoinData) {
      const opened = joinClassroom(
        pendingJoinData,
        isSafari() ? popupRef.current : null,
      );
      if (!opened) {
        return;
      }
    }
  };

  const {mutate: getActiveMeetings, isLoading: activeLoading} =
    useGetActiveMeetingsApi(
      handleOnSuccessGetActiveMeetings,
      handleOnErrorGetActiveMeetings,
    );

  const handleOnSuccessEndMeeting = (data: any) => {
    console.log('End meeting response:', data);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const handleOnErrorEndMeeting = (error: any) => {
    console.error('Error ending meetings:', error);
    setShowActiveMeetingsModal(false);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const {mutate: endClassroomMeeting, isLoading: endLoading} =
    useClassroomEndMeetingApi(
      handleOnSuccessEndMeeting,
      handleOnErrorEndMeeting,
    );
  // Main join handler
  const handleJoinClick = (joinData: JoinData) => {
    const {meeting_role, webinar_id, occurence_id} = joinData;
    console.log(meeting_role, 'meeting role');
    // SAFARI ONLY: Pre-open popup immediately on user click
    if (isSafari()) {
      popupRef.current = window.open('about:blank', '_blank');
      if (!popupRef.current) {
        // Popup was blocked even in Safari
        return false;
      }
      // Optional: Show loading message in the pre-opened popup
      try {
        popupRef.current.document.write(
          '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',sans-serif;color:#333;"><div style="text-align:center;"><div style="font-size:18px;margin-bottom:8px;">Loading Classroom</div><div style="font-size:14px;color:#666;">Please wait...</div></div></body></html>',
        );
      } catch (e) {
        console.warn('Could not write to popup:', e);
      }
    }
    // Store pending data - wait for API check before joining for all roles
    setPendingJoinData(joinData);
    getActiveMeetings({
      meetingId: webinar_id,
      occurenceId: occurence_id,
      meeting_role: meeting_role,
      classroomOrigin: 'web',
    });
    return true;
  };

  // Modal handlers
  const handleModalClose = () => {
    setShowActiveMeetingsModal(false);
    setPendingJoinData(null);
    setActiveMeetingsData(null);
    // Clean up Safari popup if exists
    if (isSafari() && popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }
  };

  const handleModalJoin = () => {
    setShowActiveMeetingsModal(false);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const handleModalEndAndJoin = () => {
    setShowActiveMeetingsModal(false);
    if (activeMeetingsData?.active_meetings && pendingJoinData) {
      const meetingIds = activeMeetingsData.active_meetings.map(
        (meeting: any) => meeting.id || meeting.meetingId || meeting.meeting_id,
      );

      endClassroomMeeting({
        meetingIds,
        meetingRole: pendingJoinData.meeting_role,
      });
    }
  };

  return {
    showActiveMeetingsModal,
    activeMeetingsCount: activeMeetingsData?.active_meetings_count || 0,
    activeLoading,
    endLoading,
    handleJoinClick,
    handleModalClose,
    handleModalJoin,
    handleModalEndAndJoin,
  };
}
